import React, { FormEvent, useCallback, useEffect, useState } from 'react';

import { APP_PERMISSIONS } from '../../constants';
import { usePermissions } from '../../hooks/usePermissions';
import { get, post, put } from '../../services/apiClient';
import { Badge } from '../common/Badge';
import { Button } from '../common/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../common/Card';
import { Input } from '../forms/Input';

interface BudgetCategory {
  id: number;
  name: string;
  description?: string | null;
  category_code?: string | null;
  fiscal_year: string;
  allocated_amount: number;
  spent_amount: number;
  committed_amount: number;
  available_amount: number;
  utilization_percentage: number;
  is_active: boolean;
}

interface VarianceAlert {
  id: string;
  alert_type: string;
  variance_amount: number;
  variance_percentage: number;
  severity: string;
  alert_message: string;
  is_resolved: boolean;
}

interface BudgetAnalytics {
  total_budget: number;
  total_spent: number;
  total_committed: number;
  total_available: number;
  utilization_rate: number;
  variance_percentage: number;
  projected_year_end: number;
  top_spending_categories: Array<[string, number]>;
  recommendations: string[];
}

interface DashboardOverview {
  overview: {
    total_budget: number;
    total_spent: number;
    total_available: number;
    utilization_rate: number;
    variance_percentage: number;
  };
  alerts: {
    total: number;
    critical: number;
    high: number;
  };
  top_categories: Array<[string, number]>;
  fiscal_year: string;
}

interface BudgetForecast {
  category_id: number;
  category_name: string;
  forecast_period_months: number;
  combined_forecast: Record<string, number>;
  assumptions: string[];
  risk_factors: string[];
}

type BudgetTab = 'overview' | 'categories' | 'forecasting' | 'analytics';

const API_BASE = '/api/budget';

const getCurrentFiscalYear = () => {
  const now = new Date();
  const startYear =
    now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  return `${startYear}-${String(startYear + 1).slice(-2)}`;
};

const getFiscalYears = () => {
  const startYear = Number(getCurrentFiscalYear().slice(0, 4));
  return Array.from({ length: 4 }, (_, index) => {
    const year = startYear - index;
    return `${year}-${String(year + 1).slice(-2)}`;
  });
};

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);

const formatPercentage = (value: number) => `${(value * 100).toFixed(1)}%`;

const formatLabel = (value: string) =>
  value
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const BudgetPlanningDashboard: React.FC = () => {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(APP_PERMISSIONS.APPROVE_EXPENSES);
  const [activeTab, setActiveTab] = useState<BudgetTab>('overview');
  const [fiscalYear, setFiscalYear] = useState(getCurrentFiscalYear);
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [categories, setCategories] = useState<BudgetCategory[]>([]);
  const [alerts, setAlerts] = useState<VarianceAlert[]>([]);
  const [analytics, setAnalytics] = useState<BudgetAnalytics | null>(null);
  const [forecasts, setForecasts] = useState<Record<number, BudgetForecast>>(
    {}
  );
  const [forecastingId, setForecastingId] = useState<number | null>(null);
  const [editingCategory, setEditingCategory] = useState<BudgetCategory | null>(
    null
  );
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({ fiscal_year: fiscalYear });
      const [overviewData, categoryData, varianceData, analyticsData] =
        await Promise.all([
          get<DashboardOverview>(`${API_BASE}/dashboard/overview?${params}`),
          get<BudgetCategory[]>(`${API_BASE}/categories?${params}`),
          get<{ alerts: VarianceAlert[] }>(`${API_BASE}/variances`),
          post<{ analytics: BudgetAnalytics }>(`${API_BASE}/analytics`, {
            fiscal_year: fiscalYear,
          }),
        ]);

      setOverview(overviewData);
      setCategories(categoryData);
      setAlerts(varianceData.alerts);
      setAnalytics(analyticsData.analytics);
    } catch (err) {
      console.error('Failed to load budget data:', err);
      setOverview(null);
      setCategories([]);
      setAlerts([]);
      setAnalytics(null);
      setError(
        err instanceof Error ? err.message : 'Budget data could not be loaded.'
      );
    } finally {
      setLoading(false);
    }
  }, [fiscalYear]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const generateForecast = async (categoryId: number) => {
    setForecastingId(categoryId);
    setError(null);
    try {
      const forecast = await post<BudgetForecast>(`${API_BASE}/forecast`, {
        category_id: categoryId,
        forecast_period_months: 12,
        include_scenarios: true,
      });
      setForecasts(current => ({ ...current, [categoryId]: forecast }));
      setActiveTab('forecasting');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'The forecast could not be generated.'
      );
    } finally {
      setForecastingId(null);
    }
  };

  const beginEdit = (category: BudgetCategory) => {
    setEditingCategory(category);
    setEditName(category.name);
    setEditDescription(category.description || '');
  };

  const saveCategory = async (event: FormEvent) => {
    event.preventDefault();
    if (!editingCategory || !editName.trim()) return;

    setSaving(true);
    setError(null);
    try {
      const updated = await put<BudgetCategory>(
        `${API_BASE}/categories/${editingCategory.id}`,
        {
          name: editName.trim(),
          description: editDescription.trim() || null,
        }
      );
      setCategories(current =>
        current.map(category =>
          category.id === updated.id ? updated : category
        )
      );
      setEditingCategory(null);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'The category could not be updated.'
      );
    } finally {
      setSaving(false);
    }
  };

  const exportReport = async () => {
    setError(null);
    try {
      const report = await get<unknown>(
        `${API_BASE}/reports/summary?fiscal_year=${encodeURIComponent(
          fiscalYear
        )}&format=json`
      );
      const blob = new Blob([JSON.stringify(report, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `budget-summary-${fiscalYear}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'The report could not be exported.'
      );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" role="status">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600" />
        <span className="ml-3 text-gray-600">Loading budget data...</span>
      </div>
    );
  }

  const summaryCards = [
    {
      label: 'Total Budget',
      value: formatCurrency(overview?.overview.total_budget ?? 0),
    },
    {
      label: 'Total Spent',
      value: formatCurrency(overview?.overview.total_spent ?? 0),
    },
    {
      label: 'Available',
      value: formatCurrency(overview?.overview.total_available ?? 0),
    },
    {
      label: 'Utilization',
      value: formatPercentage(overview?.overview.utilization_rate ?? 0),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Budget Planning</h1>
          <p className="mt-1 text-gray-600">
            Review allocations, spending, variances, and forecasts.
          </p>
        </div>
        <div className="flex gap-3">
          <select
            value={fiscalYear}
            onChange={event => setFiscalYear(event.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2"
            aria-label="Fiscal year"
          >
            {getFiscalYears().map(year => (
              <option key={year} value={year}>
                FY {year}
              </option>
            ))}
          </select>
          <Button variant="outline" onClick={() => void exportReport()}>
            Export report
          </Button>
        </div>
      </div>

      {error && (
        <Card className="border border-red-200 bg-red-50 p-5">
          <p className="font-medium text-red-800">Budget action failed</p>
          <p className="mt-1 text-sm text-red-700">{error}</p>
          <Button
            variant="outline"
            className="mt-3"
            onClick={() => void loadData()}
          >
            Try again
          </Button>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {summaryCards.map(card => (
          <Card key={card.label}>
            <CardContent className="p-5">
              <p className="text-sm font-medium text-gray-600">{card.label}</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">
                {card.value}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6 overflow-x-auto">
          {(
            [
              'overview',
              'categories',
              'forecasting',
              'analytics',
            ] as BudgetTab[]
          ).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              }`}
            >
              {formatLabel(tab)}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Variance Alerts ({alerts.length})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {alerts.length === 0 ? (
                <p className="text-gray-500">No active budget variances.</p>
              ) : (
                alerts.slice(0, 8).map(alert => (
                  <div
                    key={alert.id}
                    className="flex items-start justify-between gap-4 rounded-md border p-3"
                  >
                    <div>
                      <p className="font-medium text-gray-900">
                        {alert.alert_message}
                      </p>
                      <p className="text-sm text-gray-500">
                        {formatCurrency(Math.abs(alert.variance_amount))}
                      </p>
                    </div>
                    <Badge
                      variant={
                        ['critical', 'high'].includes(alert.severity)
                          ? 'danger'
                          : 'warning'
                      }
                    >
                      {formatLabel(alert.severity)}
                    </Badge>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Top Spending Categories</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {(overview?.top_categories ?? []).length === 0 ? (
                <p className="text-gray-500">No spending has been recorded.</p>
              ) : (
                overview?.top_categories.map(([name, amount]) => (
                  <div key={name} className="flex justify-between gap-4">
                    <span className="text-gray-700">{name}</span>
                    <span className="font-medium text-gray-900">
                      {formatCurrency(amount)}
                    </span>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'categories' && (
        <Card>
          <CardHeader>
            <CardTitle>Budget Categories ({categories.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {categories.length === 0 ? (
              <p className="p-10 text-center text-gray-500">
                No budget categories exist for FY {fiscalYear}.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50 text-left text-gray-600">
                    <tr>
                      <th className="px-5 py-3">Category</th>
                      <th className="px-5 py-3">Allocated</th>
                      <th className="px-5 py-3">Spent</th>
                      <th className="px-5 py-3">Available</th>
                      <th className="px-5 py-3">Utilization</th>
                      <th className="px-5 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {categories.map(category => (
                      <tr key={category.id}>
                        <td className="px-5 py-4">
                          <p className="font-medium text-gray-900">
                            {category.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {category.category_code || 'No category code'}
                          </p>
                        </td>
                        <td className="px-5 py-4">
                          {formatCurrency(category.allocated_amount)}
                        </td>
                        <td className="px-5 py-4">
                          {formatCurrency(category.spent_amount)}
                        </td>
                        <td className="px-5 py-4">
                          {formatCurrency(category.available_amount)}
                        </td>
                        <td className="px-5 py-4">
                          {category.utilization_percentage.toFixed(1)}%
                        </td>
                        <td className="space-x-2 px-5 py-4">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={forecastingId === category.id}
                            onClick={() => void generateForecast(category.id)}
                          >
                            {forecastingId === category.id
                              ? 'Forecasting...'
                              : 'Forecast'}
                          </Button>
                          {canEdit && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => beginEdit(category)}
                            >
                              Edit
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'forecasting' && (
        <div className="space-y-4">
          {Object.values(forecasts).length === 0 ? (
            <Card>
              <CardContent className="p-10 text-center text-gray-500">
                Generate a forecast from the Categories tab to review projected
                spending.
              </CardContent>
            </Card>
          ) : (
            Object.values(forecasts).map(forecast => (
              <Card key={forecast.category_id}>
                <CardHeader>
                  <CardTitle>
                    {forecast.category_name}: {forecast.forecast_period_months}-
                    month forecast
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {Object.entries(forecast.combined_forecast)
                      .slice(0, 12)
                      .map(([period, amount]) => (
                        <div key={period} className="rounded-md bg-gray-50 p-3">
                          <p className="text-xs text-gray-500">
                            {formatLabel(period)}
                          </p>
                          <p className="font-medium">
                            {formatCurrency(amount)}
                          </p>
                        </div>
                      ))}
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <h4 className="font-medium text-gray-900">Assumptions</h4>
                      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-600">
                        {forecast.assumptions.map(assumption => (
                          <li key={assumption}>{assumption}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">
                        Risk factors
                      </h4>
                      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-600">
                        {forecast.risk_factors.map(risk => (
                          <li key={risk}>{risk}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Year-End Projection</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Projected spending</span>
                <span className="font-medium">
                  {formatCurrency(analytics?.projected_year_end ?? 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Committed</span>
                <span className="font-medium">
                  {formatCurrency(analytics?.total_committed ?? 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Budget variance</span>
                <span className="font-medium">
                  {formatPercentage(analytics?.variance_percentage ?? 0)}
                </span>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Recommendations</CardTitle>
            </CardHeader>
            <CardContent>
              {(analytics?.recommendations ?? []).length === 0 ? (
                <p className="text-gray-500">
                  No budget recommendations are available.
                </p>
              ) : (
                <ul className="list-disc space-y-2 pl-5 text-gray-700">
                  {analytics?.recommendations.map(recommendation => (
                    <li key={recommendation}>{recommendation}</li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {editingCategory && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-budget-category-title"
        >
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle id="edit-budget-category-title">
                Edit Budget Category
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form className="space-y-4" onSubmit={saveCategory}>
                <Input
                  label="Name"
                  value={editName}
                  onChange={event => setEditName(event.target.value)}
                  required
                />
                <label className="block text-sm font-medium text-gray-700">
                  Description
                  <textarea
                    value={editDescription}
                    onChange={event => setEditDescription(event.target.value)}
                    rows={4}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
                  />
                </label>
                <div className="flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditingCategory(null)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving}>
                    {saving ? 'Saving...' : 'Save changes'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BudgetPlanningDashboard;
